import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import useChat from '../../hooks/useChat';
import Avatar from '../common/Avatar';
import { Menu, X, ChevronDown, User } from "lucide-react";
import Button from '../common/Button';

const primaryNavigation = [
  { name: "About Us", href: "/about" },
  { name: "Discover", href: "/community/discover" }
]

const authenticatedNavigation = [
  {
    name: "Community",
    href: "/community",
    children: [
      { name: "Discover", href: "/communities/discover" },
      { name: "My Communities", href: "/communities/my-communities" },
    ],
  },
  {name: "Home", href: "/"},
  // {name: "Following", href: "/profile/me/following"},
  {name: "Search", href: "/search"},
  { name: "Messages", href: "/chat", showUnreadBadge: true },
]

const unauthenticatedNavigation = [];

/**
 * Main header component for the application
 * @returns {JSX.Element} Header component
 */
const Header = () => {
  const { user, isAuth, logout } = useAuth();
  const { unreadCount, isLoadingUnreadCount } = useChat();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const pathname = useLocation().pathname;
  const isLoading = false;

  // Extract user data properly no matter how it's structured
  const userData = user?.data?.user || user?.user || user || null;

  const [authState, setAuthState] = useState(isAuth);

  useEffect(() => {
    setAuthState(isAuth);
  }, [isAuth, user]);

  // Debug output to check user data structure
  useEffect(() => {
    if (isAuth && user) {
      console.log("User data in header:", user);
    }
  }, [isAuth, user]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setIsSearchOpen(false);
      setSearchQuery('');
    }
  };

  // Helper function to render badge
  const renderBadge = (item) => {
    if (item.showUnreadBadge && authState) {
      // Show loading state with a subtle indicator
      if (isLoadingUnreadCount) {
        return (
          <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-400">
            •••
          </span>
        );
      }
      
      // Show unread count if greater than 0
      if (unreadCount > 0) {
        const displayCount = unreadCount > 99 ? '99+' : unreadCount;
        return (
          <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-red-500 text-white font-medium">
            {displayCount}
          </span>
        );
      }
      
      // Don't show badge if no unread messages
      return null;
    }
    
    // Fallback to static badge if present (backward compatibility)
    if (item.badge) {
      return (
        <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-indigo-100 text-indigo-600">
          {item.badge}
        </span>
      );
    }
    
    return null;
  };

  if (isLoading) {
    return (
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <Link to="/" className="flex items-center">
                <span className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  Vernetzung
                </span>
              </Link>
            </div>
          </div>
        </div>
      </nav>
    )
  }

  const navigationItems = authState ? [...authenticatedNavigation] : [...primaryNavigation, ...unauthenticatedNavigation];

  return (
    <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <span className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Vernetzung
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:ml-6 md:flex md:space-x-4">
              {navigationItems.map((item) => {
                if (item.children) {
                  return (
                    <div key={item.name} className="relative group flex">
                      <button
                        className={`
                          inline-flex items-center px-3 py-2 text-sm font-medium rounded-md
                          ${
                            pathname.startsWith(item.href)
                              ? "text-indigo-600"
                              : "text-gray-700 hover:text-indigo-600"
                          }
                        `}
                      >
                        {item.name}
                        <ChevronDown className="ml-1 w-4 h-4" />
                      </button>
                      <div className="absolute left-0 mt-14 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                        <div className="py-1">
                          {item.children.map((child) => (
                            <Link
                              key={child.name}
                              to={child.href}
                              className={`
                                block px-4 py-2 text-sm
                                ${
                                  pathname === child.href
                                    ? "bg-gray-100 text-indigo-600"
                                    : "text-gray-700 hover:bg-gray-50"
                                }
                              `}
                            >
                              {child.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  )
                }

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`
                      inline-flex items-center px-3 py-2 text-sm font-medium rounded-md
                      ${
                        pathname === item.href
                          ? "text-indigo-600"
                          : "text-gray-700 hover:text-indigo-600"
                      }
                    `}
                  >
                    {item.name}
                    {renderBadge(item)}
                  </Link>
                )
              })}
            </div>
          </div>

          {/* Desktop Auth Buttons */}
          <div className="hidden md:flex md:items-center md:space-x-4">
            {authState ? (
              <div className="relative group">
                <button className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:bg-gray-100">
                  {userData && (userData.profilePicture || userData.avatar || userData.image) ? (
                    <Avatar 
                      src={userData.profilePicture || userData.avatar || userData.image} 
                      alt={userData.name || userData.username || 'User'} 
                      size="sm" 
                      className="mr-2"
                    />
                  ) : (
                    <div className="mr-2">
                      <Avatar 
                        alt={userData?.name || userData?.username || 'User'} 
                        size="sm"
                      />
                    </div>
                  )}
                  <span className="max-w-[120px] truncate">
                    {userData?.name || userData?.username || 'User'}
                  </span>
                </button>
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <div className="py-1">
                    <Link 
                      to={`/profile/${userData?.username || 'me'}`}
                      className="block w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100"
                    >
                      My Profile
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="block w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100"
                    >
                      Logout
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <Link
                  to="/login"
                  className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:text-indigo-600"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:opacity-90 transition-opacity"
                >
                  Sign Up
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="flex items-center md:hidden">
            <button
              type="button"
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-indigo-600"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div className={`md:hidden ${isMobileMenuOpen ? "block" : "hidden"}`}>
        <div className="px-2 pt-2 pb-3 space-y-1">
          {navigationItems.map((item) => {
            if (item.children) {
              return (
                <div key={item.name}>
                  <button
                    className={`
                      w-full text-left px-3 py-2 text-base font-medium rounded-md
                      ${
                        pathname.startsWith(item.href)
                          ? "text-indigo-600"
                          : "text-gray-700"
                      }
                    `}
                    onClick={() => setActiveDropdown(activeDropdown === item.name ? null : item.name)}
                  >
                    <div className="flex items-center justify-between">
                      {item.name}
                      <ChevronDown className="w-4 h-4" />
                    </div>
                  </button>
                  {activeDropdown === item.name && (
                    <div className="mt-2 pl-4">
                      {item.children.map((child) => (
                        <Link
                          key={child.name}
                          to={child.href}
                          className={`
                            block px-3 py-2 text-base font-medium rounded-md
                            ${
                              pathname === child.href
                                ? "text-indigo-600"
                                : "text-gray-700"
                            }
                          `}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {child.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )
            }

            return (
              <Link
                key={item.name}
                to={item.href}
                className={`
                  block px-3 py-2 text-base font-medium rounded-md
                  ${
                    pathname === item.href ? "text-indigo-600" : "text-gray-700"
                  }
                `}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {item.name}
                {renderBadge(item)}
              </Link>
            )
          })}

          {/* Mobile Auth Buttons */}
          <div className="pt-4 pb-3 border-t border-gray-200">
            {authState ? (
              <div className="px-3 py-2">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    {userData && (userData.profilePicture || userData.avatar || userData.image) ? (
                      <Avatar 
                        src={userData.profilePicture || userData.avatar || userData.image} 
                        alt={userData.name || userData.username || 'User'} 
                        size="md" 
                      />
                    ) : (
                      <Avatar 
                        alt={userData?.name || userData?.username || 'User'} 
                        size="md"
                      />
                    )}
                  </div>
                  <div className="ml-3">
                    <div className="text-base font-medium text-gray-800">
                      {userData?.name || userData?.username || 'User'}
                    </div>
                    <div className="text-sm font-medium text-gray-500">
                      {userData?.email || (userData?.username ? `@${userData.username}` : '')}
                    </div>
                  </div>
                </div>
                <div className="mt-3 space-y-1">
                  <Link
                    to={`/profile/${userData?.username || 'me'}`}
                    className="block w-full px-3 py-2 text-base font-medium text-left text-gray-700 hover:bg-gray-100 rounded-md"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    My Profile
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMobileMenuOpen(false);
                    }}
                    className="block w-full px-3 py-2 text-base font-medium text-left text-gray-700 hover:bg-gray-100 rounded-md"
                  >
                    Logout
                  </button>
                </div>
              </div>
            ) : (
              <>
                <Link
                  to="/login"
                  className="block w-full px-3 py-2 text-base font-medium text-gray-700 hover:text-indigo-600 rounded-md"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="block w-full mt-2 px-4 py-2 text-base font-medium text-center text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-md hover:opacity-90 transition-opacity"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Sign Up
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  )
}

export default Header;